import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@shared/shared.module';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip'; // Import the module
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SharedService } from '../../services/shared.service';

export interface FilterConfig {
  showTimeline?: boolean;
  timelineLabel?: string;
  showLevel?: boolean;
  showStatus?: boolean;
  showFormat?: boolean;
  showType?: boolean;
  showViewType?: boolean;
  showLocation?: boolean;
  showGender?: boolean;
  showContest?: boolean;
  showAge?: boolean;
  showCompletionRate?: boolean;
  // Participant specific filters
  showParticipantName?: boolean;
  showParticipantEmail?: boolean;
  participantNameOptions?: { value: string; label: string }[];
  statusOptions?: { value: string; label: string }[];
  levelOptions?: number[];
  formatOptions?: { value: string; label: string }[];
  typeOptions?: { value: string; label: string }[];
  viewTypeOptions?: { value: string; label: string }[];
  locationOptions?: string[];
  genderOptions?: { value: string; label: string }[];
  contestOptions?: { value: string; label: string }[];
  // QR instance narrows a Web QR list to a single printed code. Instances
  // belong to a campaign, so the host reloads the options on contestChanged
  // and the stale selection is cleared with them.
  showQrInstance?: boolean;
  qrInstanceOptions?: { value: string; label: string }[];
  completionRateOptions?: { value: string; label: string }[];
  // Billing specific filters
  showPaymentStatus?: boolean;
  showPaymentMethod?: boolean;
  showBillingMode?: boolean;
  showPlanType?: boolean;
  showStartDate?: boolean;
  showEndDate?: boolean;
  paymentStatusOptions?: { value: string; label: string }[];
  paymentMethodOptions?: { value: string; label: string }[];
  billingModeOptions?: { value: string; label: string }[];
  planTypeOptions?: { value: string; label: string }[];
}


@Component({
  selector: 'app-filter-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, NzModalModule, NzSelectModule, NzToolTipModule,NzDatePickerModule],
  templateUrl: './filter-modal.component.html',
  styles: `  
.filter-form {
  .form-label {
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
  }
  ::ng-deep .filter-select {
    width: 100%;
    .ant-select-selector {
      border-radius: 8px;
      height: 40px !important;
      min-height: 40px !important;
      font-size: 13px;
      border: 1px solid #e4e6eb;
      display: flex;
      align-items: center;
    }
  }
  ::ng-deep .filter-select-dropdown {
    cdk-virtual-scroll-viewport {
      max-height: 202px !important;
      overflow-y: auto !important;
      scrollbar-width: thin;
      scrollbar-color: #5304FB transparent;

      &::-webkit-scrollbar {
        width: 5px;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, #8F67FD 0%, #5304FB 100%) !important;
        border-radius: 6px;

        &:hover {
          background: #4102CA !important;
        }
      }
    }
    .ant-select-item {
      min-height: 40px !important;
      height: 40px !important;
      padding: 0 14px !important;
      display: flex !important;
      align-items: center !important;
    }
  }
  input.form-control,
  .filter-input {
    width: 100%;
    border: 1px solid #e4e6eb;
    border-radius: 8px;
    height: 40px;
    font-size: 13px;
    padding: 0 12px;
  }
}
.filter-btn-outline {
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 4px 16px;
}
.ant-picker-range {
    border-radius: 4px !important;
}

.filter-btn-primary {
  background-color: #1d3557;
  border-radius: 8px;
  padding: 4px 16px;
  color: #ffffff;
}
.ant-picker {
    padding: 7px 25px 4px !important;
}
`
})
export class FilterModalComponent implements OnInit {
  @Input() isVisible: boolean = false;
  @Input() config: FilterConfig = {};
  @Input() initialFilters: any = {};
  @Input() modalTitle: string = 'Filter Campaigns';
  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() filtersApplied = new EventEmitter<any>();
  @Output() filtersReset = new EventEmitter<void>();
  /**
   * Fires when the campaign selection changes, so a host showing the QR
   * instance filter can load that campaign's instances. Emits '' for "All".
   */
  @Output() contestChanged = new EventEmitter<string>();

  filterForm!: FormGroup;
  countryRecords: { name: string }[] = [];
  defaultLevelOptions = [1, 2, 3, 4, 5];
  defaultStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];
  defaultFormatOptions = [
    { value: 'integrated', label: 'Integrated' },
    { value: 'non-integrated', label: 'Non-Integrated' }
  ];
  defaultTypeOptions = [
    { value: 'private', label: 'Private' },
    { value: 'public', label: 'Public' }
  ];
  defaultViewTypeOptions = [
    { value: 'vip', label: 'VIP' },
    { value: 'recent contest', label: 'Recent Campaign' }
  ];
  defaultGenderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'unspecified', label: 'Unspecified' }
  ];
  defaultCompletionRateOptions = [
    { value: '0-25', label: '0% - 25%' },
    { value: '26-50', label: '26% - 50%' },
    { value: '51-75', label: '51% - 75%' },
    { value: '76-100', label: '76% - 100%' }
  ];
  defaultPaymentStatusOptions = [
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' }
  ];
  defaultPaymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online' }
  ];
  defaultBillingModeOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'annually', label: 'Yearly' }
  ];
  defaultPlanTypeOptions = [
    { value: 'Premium Plan', label: 'Premium Plan' },
    { value: 'Growth', label: 'Growth' },
    { value: 'Enterprise', label: 'Enterprise' }
  ];

  constructor(private fb: FormBuilder, private sharedService: SharedService) {
    this.initializeForm();
  }
  ngOnInit() {
    this.getCountryRecords();

    // QR instances belong to one campaign, so a campaign change invalidates
    // whichever instance was picked: clear it before asking the host to load
    // the new list, or Apply would send an instance from the old campaign.
    this.filterForm.get('contest')?.valueChanges.subscribe((contestId: any) => {
      this.filterForm.get('qr_instance_id')?.setValue(null, { emitEvent: false });
      this.contestChanged.emit(contestId ? String(contestId) : '');
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialFilters'] && this.initialFilters && Object.keys(this.initialFilters).length > 0) {
      this.filterForm.patchValue(this.initialFilters, { emitEvent: false });
    }
  }
  private initializeForm(): void {
    this.filterForm = this.fb.group({
      dateRange: [null],
      level: [null],
      status: [null],
      format: [null],
      type: [null],
      viewType: [null],
      location: [[]],
      gender: [null],
      contest: [null],
      qr_instance_id: [null],
      age: [null],
      completionRate: [null],
      // Participant specific filters
      participant_name: [null],
      participant_email: [null],
      // Billing specific filters
      payment_status: [null],
      payment_method: [null],
      billing_mode: [null],
      plan_type: [null],
      start_date: [null],
      end_date: [null]
    });
  }
  handleApply(): void {
    const formValues = this.filterForm.value;

    // Prevent invalid level filter values from being applied.
    if (formValues.level !== null && formValues.level !== undefined && formValues.level !== '') {
      const parsedLevel = Number(formValues.level);
      formValues.level = Number.isFinite(parsedLevel) && parsedLevel > 0 ? parsedLevel : null;
    }

    // Remove null/empty values
    const cleanedFilters = Object.keys(formValues).reduce((acc: any, key) => {
      const value = formValues[key];
      // For arrays (like location), check if not empty
      if (Array.isArray(value)) {
        if (value.length > 0) {
          acc[key] = value;
        }
      }
      // For other values
      else if (value !== null && value !== '' && value !== undefined) {
        acc[key] = value;
      }

      return acc;
    }, {});

    this.filtersApplied.emit(cleanedFilters);
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }
  handleReset(): void {
    this.filterForm.reset({
      dateRange: null,
      level: null,
      status: null,
      format: null,
      type: null,
      viewType: null,
      location: [],
      gender: null,
      contest: null,
      qr_instance_id: null,
      age: null,
      completionRate: null,
      participant_name: null,
      participant_email: null,
      payment_status: null,
      payment_method: null,
      billing_mode: null,
      plan_type: null,
      start_date: null,
      end_date: null
    });
    this.filtersReset.emit();
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }
  handleCancel(): void {
    this.isVisible = false;
    this.isVisibleChange.emit(false);
  }

  preventNegativeNumberInput(event: KeyboardEvent): void {
    if (event.key === '-' || event.key === '+' || event.key.toLowerCase() === 'e') {
      event.preventDefault();
    }
  }

  sanitizeLevelInput(): void {
    const levelControl = this.filterForm.get('level');
    const rawValue = levelControl?.value;

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return;
    }

    const parsedLevel = Number(rawValue);
    if (!Number.isFinite(parsedLevel) || parsedLevel < 1) {
      levelControl?.setValue(null, { emitEvent: false });
    }
  }
  getCountryRecords() {
    this.sharedService.getCountryRecords().then((data) => {
      this.countryRecords = data;
    });
  }
}
